// Per-workflow File cache utility - stores File objects in memory per workflow (File objects can't be serialized) for PDF isolation. Uses Map for O(1) lookups.

// Module-level cache: Map<workflowSessionId, File[]> Using Map for better performance and memory efficiency
const workflowFilesCache = new Map();

// Cache for file lookups by name: Map<workflowSessionId, Map<fileName, File>> This allows O(1) file lookups by name instead of O(n) array searches
const workflowFilesByNameCache = new Map();

// Store files for a workflow (workflowSessionId: string, files: File[])
export const setWorkflowFiles = (workflowSessionId, files) => {
  if (!workflowSessionId) return;
  
  // Filter to only valid File objects
  const validFiles = files.filter(file => file instanceof File);
  
  if (validFiles.length > 0) {
    workflowFilesCache.set(workflowSessionId, validFiles);
    
    // Update name-based cache for O(1) lookups
    const nameCache = new Map();
    validFiles.forEach(file => {
      nameCache.set(file.name, file);
    });
    workflowFilesByNameCache.set(workflowSessionId, nameCache);
  } else {
    // If no valid files, clear both caches for this workflow
    workflowFilesCache.delete(workflowSessionId);
    workflowFilesByNameCache.delete(workflowSessionId);
  }
};

// Get files for a workflow (workflowSessionId: string) -> File[]
export const getWorkflowFiles = (workflowSessionId) => {
  if (!workflowSessionId) return [];
  
  const files = workflowFilesCache.get(workflowSessionId);
  return Array.isArray(files) ? files.filter(file => file instanceof File) : [];
};

// Add files to a workflow (append to existing) - uses name cache for O(1) duplicate checks (workflowSessionId: string, files: File[])
export const addWorkflowFiles = (workflowSessionId, files) => {
  if (!workflowSessionId) return;
  
  const existingFiles = getWorkflowFiles(workflowSessionId);
  const nameCache = workflowFilesByNameCache.get(workflowSessionId) || new Map();
  
  // Filter new files and check for duplicates using name cache
  const newFiles = files.filter(file => {
    if (!(file instanceof File)) return false;
    // O(1) duplicate check using name cache
    return !nameCache.has(file.name);
  });
  
  if (newFiles.length > 0) {
    // Add new files to name cache
    newFiles.forEach(file => {
      nameCache.set(file.name, file);
    });
    
    // Combine existing and new files
    const allFiles = [...existingFiles, ...newFiles];
    workflowFilesCache.set(workflowSessionId, allFiles);
    workflowFilesByNameCache.set(workflowSessionId, nameCache);
  }
};

// Remove a file from a workflow - uses name cache for O(1) removal (workflowSessionId: string, fileName: string)
export const removeWorkflowFile = (workflowSessionId, fileName) => {
  if (!workflowSessionId) return;
  
  const nameCache = workflowFilesByNameCache.get(workflowSessionId);
  if (nameCache && nameCache.has(fileName)) {
    // Remove from name cache
    nameCache.delete(fileName);
    
    // Update files array
    const files = getWorkflowFiles(workflowSessionId);
    const filteredFiles = files.filter(file => file.name !== fileName);
    
    if (filteredFiles.length > 0) {
      workflowFilesCache.set(workflowSessionId, filteredFiles);
      workflowFilesByNameCache.set(workflowSessionId, nameCache);
    } else {
      // Clear both caches if no files remain
      workflowFilesCache.delete(workflowSessionId);
      workflowFilesByNameCache.delete(workflowSessionId);
    }
  }
};

// Clear all files for a workflow (workflowSessionId: string)
export const clearWorkflowFiles = (workflowSessionId) => {
  if (!workflowSessionId) return;
  workflowFilesCache.delete(workflowSessionId);
  workflowFilesByNameCache.delete(workflowSessionId);
};

// Clear all files for all workflows
export const clearAllWorkflowFiles = () => {
  workflowFilesCache.clear();
  workflowFilesByNameCache.clear();
};

// Get a file by name from a workflow - optimized lookup (workflowSessionId: string, fileName: string) -> File|null
export const getWorkflowFileByName = (workflowSessionId, fileName) => {
  if (!workflowSessionId || !fileName) return null;
  
  const nameCache = workflowFilesByNameCache.get(workflowSessionId);
  return nameCache?.get(fileName) || null;
};

// Check if a file exists in a workflow - optimized check (workflowSessionId: string, fileName: string) -> boolean
export const hasWorkflowFile = (workflowSessionId, fileName) => {
  if (!workflowSessionId || !fileName) return false;
  
  const nameCache = workflowFilesByNameCache.get(workflowSessionId);
  return nameCache?.has(fileName) || false;
};

